
import type { CatalogCategory, CatalogProduct } from '../api/delivery';

type ProductWithCategory = CatalogProduct & { __categoryId: string };

// ─── Camada fixa curada ─────────────────────────────────────────────────────
//
// 12/07/2026 (a pedido do Thales: "energético + whisky tem que combinar,
// tem que melhorar a lógica"). Análise de dado real (cesta de compras do
// PDV, últimos 120 dias, 3.456 pedidos) mostrou dois tipos de padrão
// diferentes:
//
//   1) Pares de ALTO volume (cerveja+salgadinho, bomboniere+refrigerante
//      etc.) -- esses o backend já detecta sozinho via coocorrência real
//      (ver getCategoryAffinityMap em delivery.service.ts, exposto no
//      catálogo como `categoryAffinity`).
//   2) Pares ESTRATÉGICOS de baixo volume bruto que a loja já provou que
//      funcionam na prática -- ex.: Destilados x Refrigerantes/Energéticos.
//      A prova aqui não é estatística (poucas garrafas de whisky vendidas
//      no período): é que a loja já monta e vende combo pronto
//      "Destilado + 6 Red Bull + Gelo" pra praticamente toda marca
//      (Absolut, Black Label, Buchanan's, Chivas, Jack Daniels, Old Parr,
//      Red Label, Skyy, Smirnoff...). Isso também está sub-representado na
//      coocorrência bruta por categoria: quando o cliente compra o combo
//      pronto, a venda cai inteira na categoria "Combos", uma única linha
//      -- não aparecem "Destilados" e "Refrigerantes/Energéticos" como
//      categorias separadas na mesma venda pra contar como par.
//
// Por isso existe essa camada fixa: cobre pares que sabemos que funcionam
// (decisão de negócio já tomada, não amostra pequena disfarçada de
// estatística) mas que a camada dinâmica sozinha não pegaria. Mantida
// curta de propósito -- serve pra fechar essa lacuna específica, não pra
// duplicar o que a camada dinâmica já cobre bem sozinha.
//
// Chaveado por ID de categoria (não nome) porque o nome exibido no
// delivery pode ser customizado pelo dono via delivery-admin
// (delivery_category_config.custom_name) e ficaria frágil comparar string.
const FIXED_CATEGORY_AFFINITY: Record<string, string[]> = {
  'b6c1616c-7263-42e2-8592-8e765c6db037': [ // Destilados
    '3c1ebcb5-c3fe-488b-bf1a-7590d41aa2bf', // Refrigerantes & Energéticos
    'f7509391-e27e-4c0a-b4d6-3016e9127c04', // Gelo
  ],
  '78bfd86d-cec1-4847-b951-f6b602576006': [ // Vinhos & Espumantes
    'a9f206c1-927e-4c0b-b543-802535639f20', // Chocolates & Salgadinhos
  ],
  'fafef058-a08d-4e89-be2c-ec52fa8036da': [ // Cigarro
    '3c1ebcb5-c3fe-488b-bf1a-7590d41aa2bf', // Refrigerantes & Energéticos
  ],
  // 13/07/2026 (a pedido do Thales: "adicional de gelo pra quem compra
  // cerveja"): lata e long neck avulsos também puxam Gelo -- criado o
  // produto "Adicional de Gelo na Sacola" (R$10, categoria Gelo) pensado
  // exatamente pra esse cross-sell (sem peso na descrição de propósito,
  // já que não é uma quantidade medida/pesada, é só o que cabe na sacola
  // junto com a cerveja).
  '7affd24e-051f-4d77-a5c4-ac304b631823': [ // Cervejas Lata
    'f7509391-e27e-4c0a-b4d6-3016e9127c04', // Gelo
  ],
  'e6c52055-7525-4a61-84e8-718f2d4659ef': [ // Long Neck
    'f7509391-e27e-4c0a-b4d6-3016e9127c04', // Gelo
  ],
  // 13/07/2026 (dado real: dos 39 pedidos com Combo nos últimos 120 dias,
  // 17 -- 44% -- também levaram Gelo separado, mesmo o combo já incluindo
  // uma quantidade fixa de gelo/energético. Amostra pequena, mas o
  // mecanismo já existe e o custo de testar é baixo). Gelo Saborizado já
  // vem primeiro dentro do bucket (ver isGeloSaborizado abaixo), então
  // combo puxa saborizado antes do gelo comum -- pedido do Thales.
  'e5aa59e1-1d8a-4b8d-8d5d-384c1b96c3d9': [ // Combos
    'f7509391-e27e-4c0a-b4d6-3016e9127c04', // Gelo
  ],
};

// 13/07/2026 (a pedido do Thales: "dentro da pagina do produto sobre
// sugestao de gelo saborizado, acho que devia ser o padrao"). Dentro da
// categoria Gelo tem opção lisa (Gelo Cubo/Barra/Triturado) e saborizada
// (Gelo Saborizado - Coco/Maracujá/Maçã Verde/Melancia/Morango). Sem
// critério de ordenação o rail mostrava qualquer uma primeiro; agora a
// saborizada vem sempre na frente dentro do bucket de cada categoria --
// maior ticket médio por unidade e combina melhor como sugestão puxada
// por destilado/combo do que o gelo comum.
function isGeloSaborizado(name: string): boolean {
  return name.toLowerCase().includes('saborizado');
}

// 13/07/2026 (a pedido do Thales: "quando abre a lata avulsa, oferece a
// caixa fechada do mesmo produto, se tiver disponível -- vale pra lata e
// long neck"): categorias onde o upsell de "mesmo produto em caixa
// fechada" faz sentido. Escopo restrito de propósito -- outras categorias
// que também usam parentProductId (combos, retornáveis) já têm seção
// própria no site e não precisam dessa duplicata no rail.
const CASE_UPSELL_CATEGORY_IDS = new Set([
  '7affd24e-051f-4d77-a5c4-ac304b631823', // Cervejas Lata
  'e6c52055-7525-4a61-84e8-718f2d4659ef', // Long Neck
]);

// v1 (12/07/2026) -- combina 4 camadas, nessa ordem de prioridade. Nunca
// inventa popularidade/estoque -- cada camada usa só dado real que o dono
// já controla (categoria, coocorrência de pedidos reais, ou decisão de
// negócio já tomada nos combos).
//
//   1) Mesma categoria do produto aberto.
//   2) Afinidade DINÂMICA -- vem do backend (`categoryAffinity`, calculado
//      a partir de order_items reais, só com volume mínimo -- ver
//      delivery.service.ts::getCategoryAffinityMap).
//   3) Afinidade FIXA curada (acima) -- cobre pares estratégicos que a
//      dinâmica ainda não pega (baixo volume bruto ou mascarado pelos
//      combos prontos).
//   4) Destaques reais da loja (promoção/mais vendido) só pra completar o
//      rail se ainda faltar espaço.
//
// Sempre exclui o próprio produto e itens sem estoque (stock <= 0).
export function getUpsellProducts(
  categories: CatalogCategory[],
  currentProduct: CatalogProduct,
  currentCategoryId: string | null,
  categoryAffinity: Record<string, string[]> | undefined,
  limit = 6,
): CatalogProduct[] {
  const all: ProductWithCategory[] = categories.flatMap((cat) =>
    cat.products.map((p) => ({ ...p, __categoryId: cat.id })),
  );
  const available = all.filter((p) => p.id !== currentProduct.id && p.stock > 0);

  // Upsell de "mesmo produto, caixa fechada" -- não é cross-sell de
  // categoria (isso vem depois), é o MESMO item que o cliente está vendo,
  // só em quantidade fechada. parentProductId aponta pro avulso "pai";
  // filtra pelo id do produto aberto. Disponibilidade real já foi
  // confirmada pelo backend antes de expor o produto na API pública (ver
  // delivery.service.ts::effectiveStockOk) -- não precisa reconferir
  // estoque aqui além do filtro `available` acima.
  const caseUpsell: ProductWithCategory[] = currentCategoryId && CASE_UPSELL_CATEGORY_IDS.has(currentCategoryId)
    ? available.filter((p) => (p as any).parentProductId === currentProduct.id)
    : [];

  const sameCategory = currentCategoryId
    ? available.filter((p) => p.__categoryId === currentCategoryId)
    : [];

  const dynamicCategoryIds = (currentCategoryId && categoryAffinity?.[currentCategoryId]) || [];
  const fixedCategoryIds = (currentCategoryId && FIXED_CATEGORY_AFFINITY[currentCategoryId]) || [];
  const orderedRelatedIds = [...new Set([...dynamicCategoryIds, ...fixedCategoryIds])].filter(
    (id) => id !== currentCategoryId,
  );
  const relatedCategoryIds = new Set(orderedRelatedIds);

  // Intercala entre as categorias relacionadas (1 de cada por vez) em vez
  // de esgotar uma categoria antes de passar pra próxima -- senão, se
  // "Gelo" tiver poucos produtos e "Refrigerantes/Energéticos" tiver
  // muitos (ou vice-versa), uma categoria sozinha preenche todo o rail e a
  // outra nunca aparece. O ponto do Thales era "energético + whisky" --
  // as duas categorias relacionadas precisam ter chance de aparecer, não
  // só a que vier primeiro na lista.
  //
  // Dentro de cada bucket, gelo saborizado vem primeiro (ver
  // isGeloSaborizado acima) -- só reordena, não filtra nada.
  const relatedCategory: CatalogProduct[] = [];
  const relatedBuckets = orderedRelatedIds.map((id) =>
    available
      .filter((p) => p.__categoryId === id)
      .sort((a, b) => Number(isGeloSaborizado(b.name)) - Number(isGeloSaborizado(a.name))),
  );
  let anyLeft = true;
  while (anyLeft) {
    anyLeft = false;
    for (const bucket of relatedBuckets) {
      const next = bucket.shift();
      if (next) {
        relatedCategory.push(next);
        anyLeft = true;
      }
    }
  }

  const highlights = available.filter(
    (p) =>
      p.__categoryId !== currentCategoryId &&
      !relatedCategoryIds.has(p.__categoryId) &&
      (p.isPromo || p.isBestSeller),
  );

  // Preenchimento por cota, não sequencial puro. Categoria com muito
  // produto (ex.: Destilados tem 32 itens nessa loja) NÃO pode engolir o
  // rail inteiro só com "mais do mesmo" -- o objetivo aqui é cross-sell
  // (o cliente já sabe que quer whisky; o rail existe pra lembrar do
  // energético/gelo, não pra listar outro whisky). Por isso a categoria
  // relacionada tem prioridade sobre same-category quando os dois
  // competem por espaço; same-category serve só de complemento no início
  // (variações do mesmo tipo) e no fim (se sobrar vaga).
  const SAME_CATEGORY_CAP = 2;

  const seen = new Set<string>();
  const result: CatalogProduct[] = [];

  // 0) caixa fechada do mesmo produto, se existir e tiver disponível --
  //    prioridade máxima, vem antes de tudo (ver caseUpsell acima)
  for (const p of caseUpsell) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }
  // 1) até SAME_CATEGORY_CAP variações do mesmo tipo (ex.: outro whisky)
  let sameCount = 0;
  for (const p of sameCategory) {
    if (sameCount >= SAME_CATEGORY_CAP || result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
    sameCount++;
  }
  // 2) cross-sell real (dinâmico + fixo) -- prioridade sobre o resto da
  //    mesma categoria, é o motivo do rail existir
  for (const p of relatedCategory) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }
  // 3) se ainda sobrar vaga: mais variações da mesma categoria
  for (const p of sameCategory) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }
  // 4) por último, destaques reais da loja pra não deixar vaga vazia
  for (const p of highlights) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }

  return result;
}


// ─── Upsell no carrinho ─────────────────────────────────────────────────────
//
// 13/07/2026 (a pedido do Thales: "pra ter upsell a pessoa teria que
// entrar na página do produto, mas do jeito que tá não tem como fazer o
// upsell" -- observação correta: o botão "Adicionar" direto no grid da
// vitrine NUNCA passa pela ProductPage, então boa parte dos clientes
// pode nunca ver o rail de getUpsellProducts acima). O carrinho é o único
// ponto que TODO cliente passa antes de fechar o pedido, independente de
// como adicionou os itens -- por isso o rail entra aqui também, não só na
// página do produto.
//
// Mesma lógica de camadas de getUpsellProducts, mas agregada: em vez de
// "uma categoria aberta", considera TODAS as categorias já presentes no
// carrinho, e sugere com base na união das relações (dinâmica + fixa) de
// cada uma. Exclui sempre o que já está no carrinho.
export function getCartUpsellProducts(
  categories: CatalogCategory[],
  cartProductIds: Set<string>,
  categoryAffinity: Record<string, string[]> | undefined,
  limit = 6,
): CatalogProduct[] {
  const all: ProductWithCategory[] = categories.flatMap((cat) =>
    cat.products.map((p) => ({ ...p, __categoryId: cat.id })),
  );
  const productCategoryById = new Map(all.map((p) => [p.id, p.__categoryId]));
  const available = all.filter((p) => !cartProductIds.has(p.id) && p.stock > 0);

  const cartCategoryIds = new Set(
    all.filter((p) => cartProductIds.has(p.id)).map((p) => p.__categoryId),
  );

  // 0) caixa fechada de algum produto avulso já no carrinho (lata/long
  //    neck) -- mesmo princípio do rail da ProductPage: se o cliente já
  //    colocou latas avulsas, sugere fechar a caixa. Checa a categoria do
  //    PAI (avulso), não da caixa em si (a caixa pode estar em "Packs &
  //    Fardos", categoria diferente do avulso).
  const caseUpsell = available.filter((p) => {
    const parentId = (p as any).parentProductId;
    if (parentId == null || !cartProductIds.has(parentId)) return false;
    const parentCategoryId = productCategoryById.get(parentId);
    return parentCategoryId != null && CASE_UPSELL_CATEGORY_IDS.has(parentCategoryId);
  });

  // 1) categorias relacionadas (dinâmica + fixa) de QUALQUER categoria já
  //    presente no carrinho, excluindo categorias que já estão no carrinho
  const relatedCategoryIds = new Set<string>();
  for (const catId of cartCategoryIds) {
    const dyn = categoryAffinity?.[catId] || [];
    const fix = FIXED_CATEGORY_AFFINITY[catId] || [];
    for (const id of [...dyn, ...fix]) {
      if (!cartCategoryIds.has(id)) relatedCategoryIds.add(id);
    }
  }

  // Mesmo critério de intercalação por bucket do rail da ProductPage --
  // categoria com muito produto não pode engolir o espaço todo, e gelo
  // saborizado vem primeiro dentro do bucket de Gelo.
  const relatedCategory: CatalogProduct[] = [];
  const relatedBuckets = Array.from(relatedCategoryIds).map((id) =>
    available
      .filter((p) => p.__categoryId === id)
      .sort((a, b) => Number(isGeloSaborizado(b.name)) - Number(isGeloSaborizado(a.name))),
  );
  let anyLeft = true;
  while (anyLeft) {
    anyLeft = false;
    for (const bucket of relatedBuckets) {
      const next = bucket.shift();
      if (next) {
        relatedCategory.push(next);
        anyLeft = true;
      }
    }
  }

  // 2) fallback: destaques reais da loja (fora das categorias já
  //    presentes/relacionadas), só pra completar se sobrar vaga
  const highlights = available.filter(
    (p) => !cartCategoryIds.has(p.__categoryId) && !relatedCategoryIds.has(p.__categoryId) && (p.isPromo || p.isBestSeller),
  );

  const seen = new Set<string>();
  const result: CatalogProduct[] = [];

  for (const p of caseUpsell) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }
  for (const p of relatedCategory) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }
  for (const p of highlights) {
    if (result.length >= limit) break;
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    result.push(p);
  }

  return result;
}
